import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { Search } from "components/DesignSystem/Search/Search";
import Table from "components/DesignSystem/Table/V2/Table";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { EmptyScreen } from "pages/Books/EmptyScreen";
import React, { useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import {
  Profile,
  useGetAllPreGroupProfilesQuery,
} from "store/apis/salesCrmProfile";
import { ApproveModal, RejectModal } from "./StatusModal";
import CrossRed from "static/images/CrossRed.svg";
import GreenTick from "static/images/GreenTick.svg";
import { usePageTitle } from "hooks/usePageTitle";
import { CrmUnarchiveAction } from "components/CrmTableDropdown/CrmUnarchiveActionDropdown";
import { useDebounce } from "utils/debounce";
import { SolidCheck } from "components/icons/SolidCheck";
import { SolidCross } from "components/icons/SolidCross";
import { Tag } from "components/DesignSystem/Tag/Tag";

export const ArchivedUserCrm = () => {
  usePageTitle("Archived Users");
  const { update } = useUpdateQuery();
  const [pageNumber, setPageNumber] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [approveUserProfileId, setApproveUserProfileId] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const query = useQuery();
  const searchValue = query.get("search");
  const searchTerm = useDebounce(searchValue);

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    update({ query: "search", value: e.target.value || null });
  };

  const {
    data: preGroupProfiles,
    isSuccess,
    isLoading,
  } = useGetAllPreGroupProfilesQuery({
    pageNumber,
    searchTerm,
    archived: true,
  });

  const {
    profiles = [],
    total_pages = 0,
    total_count = 0,
    per_page = 1,
  } = preGroupProfiles || {};

  const approveHandler = (profileId: string) => {
    setShowModal(true);
    setApproveUserProfileId(profileId);
  };

  const rejectHandler = (profileId: string) => {
    setShowRejectModal(true);
    setApproveUserProfileId(profileId);
  };

  const columnHelper = createColumnHelper<Profile>();

  const columns = [
    columnHelper.display({
      id: "name",
      header: "Name",
      size: 15,
      cell: ({ row }) => {
        const { first_name, last_name } = row.original;
        return (
          <div className="t-truncate">
            {first_name} {last_name}
          </div>
        );
      },
    }),
    columnHelper.accessor("email", {
      id: "email",
      header: "Email",
      size: 15,
      cell: (info) => {
        return <div className="t-truncate">{info.getValue()}</div>;
      },
    }),
    columnHelper.accessor("is_email_verified", {
      id: "is_email_verified",
      header: "Email OTP",
      size: 10,
      cell: ({ value }: any) => (
        <>
          {value ? (
            <SolidCheck size="24" />
          ) : (
            <span className="t-flex t-text-red-70">
              <SolidCross size="24" />
            </span>
          )}
        </>
      ),
    }),
    columnHelper.accessor("is_mobile_verified", {
      id: "is_mobile_verified",
      header: "Mobile OTP",
      size: 10,
      cell: ({ value }: any) => (
        <>
          {value ? (
            <SolidCheck size="24" />
          ) : (
            <span className="t-flex t-text-red-70">
              <SolidCross size="24" />
            </span>
          )}
        </>
      ),
    }),
    columnHelper.display({
      id: "status",
      header: "Status",
      size: 20,
      cell: ({ row }: any) => {
        if (
          !row.original?.is_email_verified &&
          !row.original?.is_mobile_verified
        ) {
          return <div>-</div>;
        }
        if (row.original?.is_rejected) {
          return <Tag tagType="red">Rejected </Tag>;
        }
        if (row.original?.is_restricted) {
          return (
            <div className="t-flex t-gap-2">
              <Button onClick={() => approveHandler(row.original?.uuid)}>
                Approve
              </Button>
              <Button
                customType="secondary"
                onClick={() => {
                  rejectHandler(row.original?.uuid);
                }}
              >
                Reject
              </Button>
            </div>
          );
        }
        if (!row.original?.is_restricted && !row.original?.is_rejected) {
          return <Tag tagType="green">Approved</Tag>;
        }
      },
    }),
    columnHelper.display({
      id: "group",
      header: "Group",
      size: 20,
      cell: ({ row }) => {
        const { group } = row.original;
        return <div>{group?.name}</div>;
      },
    }),
    columnHelper.display({
      id: "threedots",
      header: "",
      size: 5,
      cell: (info) => {
        const { uuid, name } = info.row.original;
        return <CrmUnarchiveAction actionOn={"profile"} uuid={uuid!} />;
      },
    }),
  ];

  const table = useReactTable({
    data: profiles,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      minSize: 1,
    },
  });

  const isEmpty = profiles.length === 0;

  return (
    <div className="t-h-full t-overflow-hidden t-flex t-flex-col">
      <div className="t-pb-5 t-flex t-justify-between t-items-center t-shrink-0">
        <div className="t-w-1/2">
          <Search
            block
            onChange={onSearch}
            value={searchValue || ""}
            placeholder="Search"
          />
        </div>
        <PaginationHandler
          page_num={pageNumber}
          per_page={per_page}
          total_count={total_count}
          total_pages={total_pages}
          onPageNumChange={setPageNumber}
        />
      </div>
      <div className="t-flex-1 t-min-h-0 t-overflow-auto">
        <Async.Root {...{ isLoading, isEmpty, isSuccess }}>
          <Async.Empty>
            <EmptyScreen text="No users found" />
          </Async.Empty>
          <Async.Success>
            <Table.Container sticky style={{ height: "100%" }}>
              <Table.Content>
                <Table.Head>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <Table.Row key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <Table.HeadCell
                          key={header.id}
                          style={{ width: `${header.getSize()}%` }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </Table.HeadCell>
                      ))}
                    </Table.Row>
                  ))}
                </Table.Head>
                <Table.Body>
                  {table.getRowModel().rows.map((row) => (
                    <Table.Row key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <Table.Cell
                          key={cell.id}
                          style={{ width: `${cell.column.getSize()}%` }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Content>
            </Table.Container>
            <ApproveModal
              showModal={showModal}
              setShowModal={setShowModal}
              approveUserProfileId={approveUserProfileId}
            />
            <RejectModal
              showRejectModal={showRejectModal}
              setShowRejectModal={setShowRejectModal}
              approveUserProfileId={approveUserProfileId}
            />
          </Async.Success>
        </Async.Root>
      </div>
    </div>
  );
};
